import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CheckCheck, FileJson, Send, CheckCircle, XCircle, ChevronUp, ChevronDown, Plus, Trash2, Edit } from 'lucide-react';

// ====================================================================
// JHipster API Client Import
// ====================================================================
// Assuming the path to your JHipster API client utility is correct
import api from '../utils/api';
import Layout from './layout/DefaultLayout';
import QuestionRenderer from './assessment/QuestionRenderer';
import { MathJaxContext } from 'better-react-mathjax';
import { convertMathJax } from '../utils/QuestionsUtils';


// ====================================================================
// 1. UTILITY & CONSTANTS
// ====================================================================

// The MathJax component to render LaTeX math expressions.
const MathText = React.memo(({ content }) => {
    return (
        <MathJaxContext>
            {convertMathJax(content)}
        </MathJaxContext >
    )
});


// ====================================================================
// 2. PRESENTATIONAL COMPONENTS (Editor and Preview)
// ====================================================================

// A custom multi-select component for tags
const MultiSelectTags = ({ options, selectedTags, onToggleTag }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef(null);

    const handleToggle = (tag) => {
        onToggleTag(tag);
    };

    const isSelected = (tag) => {
        return selectedTags?.some(t => t.id === tag.id);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [ref]);

    return (
        <div className="relative" ref={ref}>
            <div
                className="mt-1 flex flex-wrap items-center w-full px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm cursor-pointer min-h-[44px]"
                onClick={() => setIsOpen(!isOpen)}
            >
                {selectedTags?.length > 0 ? (
                    selectedTags.map(tag => (
                        <span key={tag.id} className="inline-flex items-center text-sm font-medium bg-gray-200 text-gray-800 rounded-full px-3 py-1 mr-2 my-1">
                            {options.find(o => o.id === tag.id)?.name || 'Unknown'}
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleToggle(tag); }}
                                className="ml-1 text-gray-500 hover:text-gray-900 focus:outline-none"
                            >
                                &times;
                            </button>
                        </span>
                    ))
                ) : (
                    <span className="text-gray-400">Select tags...</span>
                )}
                <ChevronDown size={16} className={`ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </div>
            {isOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-y-auto">
                    {options.map(tag => (
                        <div
                            key={tag.id}
                            onClick={() => handleToggle(tag)}
                            className="px-4 py-2 cursor-pointer hover:bg-indigo-50 flex items-center justify-between"
                        >
                            {tag.name}
                            {isSelected(tag) && <CheckCheck size={16} className="text-green-500" />}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
// Assuming MathText is a custom component for rendering math/markdown

// Helper function to normalize displayOrder: sorts details and re-assigns 1, 2, 3...
const normalizeDisplayOrder = (details) => {
    // ... (normalizeDisplayOrder function remains the same as before)
    const sortedDetails = [...details].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    return sortedDetails.map((d, index) => ({
        ...d,
        displayOrder: index + 1,
    }));
};

// --- NEW UTILITY: Define which fields are relevant for each question type ---
const FIELD_MAP = {
    MULTIPLE_CHOICE: [
        { field: 'questionChoice', label: 'Option Text', required: true, type: 'textarea' },
        { field: 'explanation', label: 'Explanation (Review)', type: 'textarea' },
        { field: 'hint', label: 'Hint (Optional)', type: 'textarea' },
    ],
    OPEN_ENDED: [
        { field: 'questionChoice', label: 'Sub-Question/Prompt Text', required: true, type: 'textarea' },
        { field: 'explanation', label: 'Explanation (Review)', type: 'textarea' },
        { field: 'hint', label: 'Hint (Optional)', type: 'textarea' },
    ],
    MATCHING: [
        { field: 'questionLeft', label: 'Left Column Items (Comma-separated)', required: true, type: 'textarea' },
        { field: 'questionRight', label: 'Right Column Items (Comma-separated)', required: true, type: 'textarea' },
        { field: 'explanation', label: 'Explanation (Review)', type: 'textarea' },
        { field: 'hint', label: 'Hint (Optional)', type: 'textarea' },
    ],
    QUESTION_TABLE: [
        { field: 'questionCsv', label: 'CSV Data (Rows separated by \\n, cells by ,)', required: true, type: 'textarea' },
        { field: 'explanation', label: 'Explanation (Review)', type: 'textarea' },
        { field: 'hint', label: 'Hint (Optional)', type: 'textarea' },
    ],
    MATHS_CALCULATION: [
        { field: 'questionChoice', label: 'Option Text', required: true, type: 'textarea' },
        { field: 'questionMarkup', label: 'Calculation/Formula Markup', required: true, type: 'textarea' },
        { field: 'explanation', label: 'Explanation (Review)', type: 'textarea' },
        { field: 'hint', label: 'Hint (Optional)', type: 'textarea' },
    ],
    // Default or other types can show a minimal set
    DEFAULT: [
        { field: 'questionChoice', label: 'Content Text', required: false, type: 'textarea' },
    ],
};
// --------------------------------------------------------------------------

const QuestionDetailEditor = ({ question, onUpdateDetails }) => {
    const details = question.details.map((item, index) => {
        const newId = index + 1;
        return {
            ...item,
            id: newId // The integer ID is added here
        };
    });
    const questionType = question.questionType; // Get the type to control rendering

    // The fields to display, based on the question type
    const fieldsToRender = FIELD_MAP[questionType] || FIELD_MAP.DEFAULT;

    // ... (handleDetailFieldChange, handleReorder, generateUniqueId, handleAddDetail, handleRemoveDetail functions remain the same as the improved version)

    const handleDetailFieldChange = (detailId, field, value) => {
        let updatedDetails = details.map(d =>
            d.id === detailId ? { ...d, [field]: value } : d
        );
        if (field === 'displayOrder') {
            updatedDetails = normalizeDisplayOrder(updatedDetails);
        }
        const detailsWithoutId = updatedDetails.map(({ id, ...rest }) => {
            return rest;
        });
        onUpdateDetails(detailsWithoutId);
    };

    const handleReorder = (detailId, direction) => {
        const detailIndex = details.findIndex(d => d.id === detailId);
        if (detailIndex < 0) return;
        const currentOrder = details[detailIndex].displayOrder;
        const targetOrder = currentOrder + direction;
        if (targetOrder >= 1 && targetOrder <= details.length) {
            handleDetailFieldChange(detailId, 'displayOrder', targetOrder);
        }
    };

    const generateUniqueId = () =>
        `detail-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    const handleAddDetail = () => {
        const newId = generateUniqueId();
        const newDetail = {
            id: newId,
            questionChoice: "New Sub-Question Text",
            questionLeft: "",
            questionRight: "",
            questionCsv: "",
            questionMarkup: "",
            explanation: "",
            hint: "",
            displayOrder: details.length + 1,
        };
        onUpdateDetails(normalizeDisplayOrder([...details, newDetail]));
    };

    const handleRemoveDetail = (detailId) => {
        if (details.length <= 1) {
            window.alert("You must have at least one question detail (sub-question).");
            return;
        }
        const updatedDetails = details.filter(d => d.id !== detailId);
        onUpdateDetails(normalizeDisplayOrder(updatedDetails));
    };


    return (
        <div className="bg-purple-50 p-6 rounded-lg shadow-inner border border-purple-300 space-y-4">
            <h3 className="text-xl font-bold text-purple-700 mb-4">
                Question Details Configuration ({questionType ? questionType.replace(/_/g, ' ') : 'General'})
            </h3>

            {details
                .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                .map((detail) => (
                    <div key={detail.id} className="border border-purple-200 p-4 rounded-md bg-white space-y-3">

                        {/* HEADER & CONTROLS */}
                        <div className="flex justify-between items-start">
                            <h4 className="font-semibold text-gray-800">Sub-Question {detail.displayOrder}</h4>
                            <div className="flex items-center space-x-2">
                                {/* Order Controls */}
                                <div className="flex items-center space-x-1 border border-gray-300 rounded-md p-0.5">
                                    <button
                                        onClick={() => handleReorder(detail.id, -1)} // Move Up
                                        disabled={detail.displayOrder === 1}
                                        className="p-1 text-gray-500 hover:text-purple-700 disabled:opacity-50 transition"
                                    >
                                        <ChevronUp size={16} />
                                    </button>
                                    <span className="text-sm font-semibold w-4 text-center">{detail.displayOrder}</span>
                                    <button
                                        onClick={() => handleReorder(detail.id, 1)} // Move Down
                                        disabled={detail.displayOrder === details.length}
                                        className="p-1 text-gray-500 hover:text-purple-700 disabled:opacity-50 transition"
                                    >
                                        <ChevronDown size={16} />
                                    </button>
                                </div>
                                {/* Remove Button */}
                                <button
                                    onClick={() => handleRemoveDetail(detail.id)}
                                    className="p-1 text-red-500 hover:text-red-700 transition"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>

                        {/* DYNAMIC FIELD RENDERING */}
                        {fieldsToRender.map(({ field, label, required, type }, idx) => (
                            <div key={field}>
                                <label className="block text-sm font-medium text-gray-700">
                                    <MathText content={label} /> {required && <span className="text-red-500">*</span>}
                                </label>
                                {type === 'textarea' ? (
                                    <textarea
                                        rows={field === 'questionCsv' ? 4 : 2}
                                        value={detail[field] || ''}
                                        onChange={(e) => handleDetailFieldChange(detail.id, field, e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                        required={required}
                                        placeholder={field === 'questionCsv' ? "e.g., Header1,Header2\nRowA,Value1" : ""}
                                    />
                                ) : (
                                    // Add other input types here if needed (e.g., type === 'text')
                                    <input
                                        type="text"
                                        value={detail[field] || ''}
                                        onChange={(e) => handleDetailFieldChange(detail.id, field, e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                        required={required}
                                    />
                                )}
                                {/* For fields that are often rendered, show a preview */}
                                {(
                                    <div className="mt-1">
                                        <label className="block text-xs font-medium text-gray-500">Render Preview:</label>
                                        <div className="p-2 border rounded bg-gray-50"><MathText content={detail[field]} /></div>
                                    </div>
                                )}
                            </div>
                        ))}

                    </div>
                ))}

            {/* ADD BUTTON */}
            <div className="text-center mt-4">
                <button
                    onClick={handleAddDetail}
                    className="flex items-center justify-center space-x-2 px-4 py-2 text-sm bg-purple-500 text-white rounded-full hover:bg-purple-600 transition shadow-lg mx-auto"
                >
                    <Plus size={16} />
                    <span>Add New Sub-Question Detail</span>
                </button>
            </div>
        </div>
    );
};

const AnswerChoiceEditor = ({ answerChoices, onUpdateChoices }) => {
    const handleChoiceFieldChange = (choiceId, field, value) => {
        const updatedChoices = answerChoices.map(ac =>
            ac.id === choiceId ? { ...ac, [field]: value } : ac
        );
        onUpdateChoices(updatedChoices);
    };

    const handleCorrectToggle = (choiceId) => {
        const updatedChoices = answerChoices.map(ac => ({
            ...ac,
            isCorrect: ac.id === choiceId ? !ac.isCorrect : false,
        }));
        onUpdateChoices(updatedChoices);
    };

    const handleAddChoice = () => {
        const newId = Math.max(...answerChoices.map(c => c.id), 0) + 1;
        const newChoice = {
            id: newId,
            choiceText: "New Choice",
            isCorrect: false,
            explanation: ""
        };
        onUpdateChoices([...answerChoices, newChoice]);
    };

    const handleRemoveChoice = (choiceId) => {
        if (answerChoices.length <= 1) {
            window.alert("You must have at least one answer choice.");
            return;
        }
        const updatedChoices = answerChoices.filter(ac => ac.id !== choiceId);
        onUpdateChoices(updatedChoices);
    };

    return (
        <div className="bg-yellow-50 p-6 rounded-lg shadow-inner border border-yellow-300 space-y-4">
            <h3 className="text-xl font-bold text-yellow-700 mb-4">Answer Choices Configuration</h3>
            {answerChoices.map((choice, index) => (
                <div key={choice.id} className="border border-yellow-200 p-4 rounded-md bg-white">
                    <div className="flex justify-between items-start mb-3">
                        <h4 className="font-semibold text-gray-800">Choice {index + 1}</h4>
                        <div className="flex items-center space-x-2">
                            <span className={`text-sm font-medium p-1 rounded ${choice.isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                {choice.isCorrect ? 'CORRECT' : 'INCORRECT'}
                            </span>
                            <button
                                onClick={() => handleCorrectToggle(choice.id)}
                                className={`px-3 py-1 text-xs rounded transition duration-150 ${choice.isCorrect ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-green-500 hover:bg-green-600 text-white'}`}
                            >
                                {choice.isCorrect ? 'Mark Incorrect' : 'Mark Correct'}
                            </button>
                            <button
                                onClick={() => handleRemoveChoice(choice.id)}
                                className="p-1 text-red-500 hover:text-red-700 transition"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-500">Rendered:</label>
                        <div className="p-2 border rounded"><MathText content={choice.choiceText} /></div>
                    </div>
                    <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700">Choice Text (Edit) <span className="text-red-500">*</span></label>
                        <textarea
                            rows={2}
                            value={choice.choiceText}
                            onChange={(e) => handleChoiceFieldChange(choice.id, 'choiceText', e.target.value)}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Explanation</label>
                        <textarea
                            rows={2}
                            value={choice.explanation || ''}
                            onChange={(e) => handleChoiceFieldChange(choice.id, 'explanation', e.target.value)}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                        />
                    </div>
                </div>
            ))}
            <div className="text-center mt-4">
                <button
                    onClick={handleAddChoice}
                    className="flex items-center justify-center space-x-2 px-4 py-2 text-sm bg-indigo-500 text-white rounded-full hover:bg-indigo-600 transition shadow-lg mx-auto"
                >
                    <Plus size={16} />
                    <span>Add New Choice</span>
                </button>
            </div>
        </div>
    );
};

// A component to display a single question in a user-facing preview format.
const AssessmentComponent = ({ question, onEdit, onReviewStatusChange, validationErrors }) => {
    // Gracefully handle case where question is null
    if (!question) return null;

    const questionType = question.questionType.replace(/_/g, ' ');
    const hasErrors = Object.keys(validationErrors).length > 0;

    return (
        <div className="p-8 bg-gray-100 min-h-screen">
            <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-2xl border border-indigo-100 p-8 space-y-6">
                <div className="flex justify-between items-center pb-4 border-b">
                    <div>
                        <h2 className="text-2xl font-bold text-indigo-600">Question Preview</h2>
                        <span className="text-sm font-medium text-gray-500">{questionType}</span>
                    </div>
                    <button
                        onClick={onEdit}
                        className="flex items-center space-x-2 px-4 py-2 bg-indigo-500 text-white rounded-full hover:bg-indigo-600 transition shadow-lg"
                    >
                        <Edit size={16} />
                        <span>Edit Question</span>
                    </button>
                </div>

                {hasErrors && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                        <strong className="font-bold">Validation Errors!</strong>
                        <span className="block sm:inline ml-2">Approve button disabled.</span>
                        <ul className="mt-2 list-disc list-inside">
                            {Object.values(validationErrors).map((error, index) => (
                                <li key={index}>{error}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="bg-blue-50 p-6 rounded-lg border border-blue-200 space-y-4">
                    <h3 className="text-xl font-bold text-blue-700 mb-2">Review Status</h3>
                    <div className="flex items-center space-x-4">
                        <span className={`px-4 py-2 rounded-full font-semibold text-sm ${question.reviewStatus === 'approved' ? 'bg-green-100 text-green-700' :
                            question.reviewStatus === 'rejected' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-700'
                            }`}>
                            Status: {question.reviewStatus.toUpperCase()}
                        </span>
                        <button
                            onClick={() => onReviewStatusChange('approved')}
                            className={`flex items-center space-x-1 px-4 py-2 text-white rounded-full transition ${hasErrors ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600'}`}
                            disabled={hasErrors}
                        >
                            <CheckCircle size={16} />
                            <span>Approve</span>
                        </button>
                        <button
                            onClick={() => onReviewStatusChange('rejected')}
                            className={`flex items-center space-x-1 px-4 py-2 text-white rounded-full transition ${hasErrors ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'}`}
                            disabled={hasErrors}
                        >
                            <XCircle size={16} />
                            <span>Reject</span>
                        </button>
                    </div>
                </div>

                <div className="p-4 rounded-md border border-gray-300">
                    <h3 className="text-xl font-semibold mb-2 text-gray-800">Question</h3>
                    <div className="text-lg leading-relaxed">
                        <QuestionRenderer question={question} index={0} />
                    </div>
                </div>
                {/* {question.pictureUrl && (
                    <div className="p-4 rounded-md border border-yellow-300 bg-yellow-50">
                        <h4 className="text-sm font-semibold text-yellow-700">Associated Image</h4>
                        <img
                            src={`https://placehold.co/400x300/e5e7eb/4b5563?text=Image+Placeholder`}
                            alt="Placeholder"
                            className="mt-2 rounded-md border border-gray-300 shadow-sm max-w-full h-auto"
                        />
                    </div>
                )} */}

                {/* START: Question Details/Sub-Questions Preview */}
                {question.details && question.details.length > 0 && (
                    <div className="space-y-4 p-4 rounded-md border border-purple-300 bg-purple-50">
                        <h3 className="text-xl font-semibold text-purple-700">Question Details/Sub-Questions</h3>
                        {question.details
                            .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                            .map((detail) => (
                                <>
                                    <div key={detail.id} className="p-3 rounded-lg border border-purple-200 bg-white">
                                        <div className="flex-1 text-lg">
                                            <MathText content={detail.questionChoice || detail.questionCsv
                                                || detail.questionLeft || detail.questionMarkup} />
                                        </div>

                                        {detail.explanation && (
                                            <div className="mt-2 pt-2 border-t border-dashed border-gray-200">
                                                <span className="text-sm font-semibold text-gray-600">Explanation:</span>
                                                <p className="text-sm text-gray-500"><MathText content={detail.explanation} /></p>
                                            </div>
                                        )}
                                        {detail.hint && (
                                            <div className="mt-2 pt-2 border-t border-dashed border-gray-200">
                                                <span className="text-sm font-semibold text-gray-600">Hint:</span>
                                                <p className="text-sm text-gray-500"><MathText content={detail.hint} /></p>
                                            </div>
                                        )}
                                    </div>
                                    {detail.questionRight && (<div key={detail.id} className="p-3 rounded-lg border border-purple-200 bg-white">
                                        <div className="flex-1 text-lg">
                                            <MathText content={detail.questionRight} />
                                        </div>
                                        {detail.explanation && (
                                            <div className="mt-2 pt-2 border-t border-dashed border-gray-200">
                                                <span className="text-sm font-semibold text-gray-600">Explanation:</span>
                                                <p className="text-sm italic text-gray-500"><MathText content={detail.explanation} /></p>
                                            </div>
                                        )}
                                    </div>
                                    )}
                                </>
                            ))}
                    </div>
                )}
                {/* END: Question Details/Sub-Questions Preview */}

                {/* Answer Choices - Show multi-part answer summary */}
                {question.answerChoices && question.answerChoices.length > 0 && (
                    <div className="space-y-4">
                        <h3 className="text-xl font-semibold text-gray-800">Answer Choices</h3>
                        {question.answerChoices.map((choice, index) => (
                            <div key={choice.id} className="p-3 rounded-lg border border-purple-200 bg-white">
                                <div key={choice.id} className={`p-4 rounded-lg border flex items-start space-x-4 ${choice.isCorrect ? 'bg-green-100 border-green-300' : 'bg-white border-gray-200'}`}>
                                    <span className="text-lg font-bold text-gray-600">{String.fromCharCode(65 + index)}.</span>
                                    <div className="flex-1 text-lg">
                                        <MathText content={choice.choiceText} />
                                    </div>

                                </div>
                                <div>
                                    {
                                        choice.explanation && (
                                            <div className="mt-2 pt-2 border-t border-dashed border-gray-200">
                                                <span className="text-sm font-semibold text-gray-600">Explanation:</span>
                                                <p className="text-sm text-gray-500"><MathText content={choice.explanation} /></p>
                                            </div>
                                        )
                                    }
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const QuestionEditor = ({ question, onSave, onCancel, sources, subjects, chapters, classStandards, tags, isLoading }) => {
    const [localQuestion, setLocalQuestion] = useState({
        ...question,
        tags: question.tags || [],
        details: question.details || [],
        answerChoices: question.answerChoices || []
    });

    useEffect(() => {
        setLocalQuestion({
            ...question,
            tags: question.tags || [],
            details: question.details || [],
            answerChoices: question.answerChoices || []
        });
    }, [question]);

    const handleChange = (field, value) => {
        let updatedValue = value;
        // Convert the dropdown value (ID) into the correct JSON object structure { id: ... }
        if (['source', 'subject', 'chapter', 'classStandard'].includes(field)) {
            updatedValue = value ? { id: parseInt(value, 10) } : null;
        }
        setLocalQuestion(prev => ({ ...prev, [field]: updatedValue }));
    };

    const handleTagsUpdate = useCallback((tag) => {
        setLocalQuestion(prev => {
            const isSelected = prev.tags.some(t => t.id === tag.id);
            // Need the full tag object from the options to toggle/add it correctly
            const fullTag = tags.find(t => t.id === tag.id) || tag;
            if (isSelected) {
                return {
                    ...prev,
                    tags: prev.tags.filter(t => t.id !== tag.id)
                };
            } else {
                return {
                    ...prev,
                    tags: [...prev.tags, fullTag]
                };
            }
        });
    }, [tags]);

    const handleAnswerChoicesUpdate = (updatedChoices) => {
        setLocalQuestion(prev => ({ ...prev, answerChoices: updatedChoices }));
    };

    const handleDetailsUpdate = (updatedDetails) => {
        setLocalQuestion(prev => ({ ...prev, details: updatedDetails }));
    };

    const handleSubmit = () => {
        // The main app component handles full validation on review status change or on mount.
        // For the editor's save button, we simply push the change back.
        onSave(localQuestion);
    };

    const questionType = localQuestion.questionType.replace(/_/g, ' ');

    // --- Chapter Filtering Logic ---
    const selectedSubjectId = localQuestion.subject?.id;
    const selectedClassStandardId = localQuestion.classStandard?.id;
    // Assuming chapters object has a subject property { id: ... }
    const filteredChapters = selectedSubjectId
        ? chapters.filter(c => c.subject?.id === selectedSubjectId && c.classStandard?.id === selectedClassStandardId)
        : chapters;


    return (
        <div className="p-8 bg-white rounded-xl shadow-2xl border border-indigo-100 space-y-6">
            <div className="flex justify-between items-center border-b pb-4">
                <h2 className="text-2xl font-bold text-indigo-600">Editing: {questionType}</h2>
                <span className={`px-3 py-1 text-sm font-semibold rounded-full ${localQuestion.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {localQuestion.isActive ? 'Active' : 'Inactive'}
                </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-lg bg-gray-50 border">
                {/* 1. Class Standard */}
                <div>
                    <label htmlFor="classStandard-select" className="block text-sm font-medium text-gray-700">Class Standard <span className="text-red-500">*</span></label>
                    <select
                        id="classStandard-select"
                        value={localQuestion.classStandard?.id || ''}
                        onChange={(e) => handleChange('classStandard', e.target.value)}
                        className="mt-1 block w-full px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm"
                        disabled={isLoading}
                        required
                    >
                        {isLoading && <option value="">Loading...</option>}
                        {!isLoading && <option value="">Select a Class Standard...</option>}
                        {classStandards.map(standard => (
                            <option key={standard.id} value={standard.id}>{standard.name}</option>
                        ))}
                    </select>
                </div>
                {/* 2. Source */}
                <div>
                    <label htmlFor="source-select" className="block text-sm font-medium text-gray-700">Source <span className="text-red-500">*</span></label>
                    <select
                        id="source-select"
                        value={localQuestion.source?.id || ''}
                        onChange={(e) => handleChange('source', e.target.value)}
                        className="mt-1 block w-full px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm"
                        disabled={isLoading}
                        required
                    >
                        {isLoading && <option value="">Loading...</option>}
                        {!isLoading && <option value="">Select a Source...</option>}
                        {sources.map(source => (
                            <option key={source.id} value={source.id}>{source.name}</option>
                        ))}
                    </select>
                </div>
                {/* 3. Subject */}
                <div>
                    <label htmlFor="subject-select" className="block text-sm font-medium text-gray-700">Subject <span className="text-red-500">*</span></label>
                    <select
                        id="subject-select"
                        value={localQuestion.subject?.id || ''}
                        onChange={(e) => {
                            handleChange('subject', e.target.value);
                            // Clear chapter selection when subject changes
                            handleChange('chapter', '');
                        }}
                        className="mt-1 block w-full px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm"
                        disabled={isLoading}
                        required
                    >
                        {isLoading && <option value="">Loading...</option>}
                        {!isLoading && <option value="">Select a Subject...</option>}
                        {subjects.map(subject => (
                            <option key={subject.id} value={subject.id}>{subject.name}</option>
                        ))}
                    </select>
                </div>
                {/* 4. Chapter (Filtered) */}
                <div>
                    <label htmlFor="chapter-select" className="block text-sm font-medium text-gray-700">Chapter <span className="text-red-500">*</span></label>
                    <select
                        id="chapter-select"
                        value={localQuestion.chapter?.id || ''}
                        onChange={(e) => handleChange('chapter', e.target.value)}
                        className="mt-1 block w-full px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm"
                        disabled={isLoading || !selectedSubjectId}
                        required
                    >
                        {isLoading && <option value="">Loading...</option>}
                        {(!selectedSubjectId || !selectedClassStandardId) && <option value="">Select Subject/Class first...</option>}
                        {selectedSubjectId && selectedClassStandardId && filteredChapters.length === 0 && <option value="">No Chapters for this Subject/Class</option>}
                        {selectedSubjectId && filteredChapters.length !== 0 && <option value="">select</option>}

                        {filteredChapters.map(chapter => (
                            <option key={chapter.id} value={chapter.id}>{chapter.name}</option>
                        ))}
                    </select>
                </div>
                <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Tags</label>
                    <MultiSelectTags
                        options={tags}
                        selectedTags={localQuestion.tags}
                        onToggleTag={handleTagsUpdate}
                    />
                </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-md border">
                <label className="block text-xs font-medium text-gray-500">Rendered Question Text (MathJax Supported):</label>
                <div className="text-lg mt-1 p-2 border-dashed border-gray-300">
                    <MathText content={localQuestion.questionText} />
                </div>
                <label htmlFor="questionTextEdit" className="block text-sm font-medium text-gray-700 mt-3">Question Text (Edit) <span className="text-red-500">*</span></label>
                <textarea
                    id="questionTextEdit"
                    rows={4}
                    value={localQuestion.questionText}
                    onChange={(e) => handleChange('questionText', e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                />
                <label htmlFor="pictureUrlEdit" className="block text-sm font-medium text-gray-700 mt-3">Picture URL (Optional)</label>
                <input
                    id="pictureUrlEdit"
                    type="url"
                    value={localQuestion.pictureUrl || ''}
                    onChange={(e) => handleChange('pictureUrl', e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="e.g., https://example.com/image.png"
                />
            </div>
            {localQuestion.pictureUrl && (
                <div className="bg-yellow-50 p-4 rounded-md border border-yellow-200">
                    <h3 className="text-sm font-semibold text-yellow-700">Associated Image</h3>
                    <p className="text-sm text-yellow-600 mt-1">
                        Image URL: <a href={localQuestion.pictureUrl} target="_blank" rel="noopener noreferrer" className="underline">{localQuestion.pictureUrl}</a>
                    </p>
                    <p className="text-xs text-yellow-500 mt-2">
                        Note: A placeholder for the image is shown below since a full URL was not provided.
                    </p>
                    <img
                        src={`https://placehold.co/400x300/e5e7eb/4b5563?text=Image+Placeholder`}
                        alt="Placeholder"
                        className="mt-4 rounded-md border border-gray-300 shadow-sm"
                    />
                </div>
            )}
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label htmlFor="difficulty" className="block text-sm font-medium text-gray-700">Difficulty Level <span className="text-red-500">*</span></label>
                    <select
                        id="difficulty"
                        value={localQuestion.difficultyLevel || 'LOW'} // Default to 'LOW' if null
                        onChange={(e) => handleChange('difficultyLevel', e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                        required
                    >
                        {['LOW', 'MED', 'HIGH'].map(level => (
                            <option key={level} value={level}>{level}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label htmlFor="isActive" className="block text-sm font-medium text-gray-700">Status <span className="text-red-500">*</span></label>
                    <select
                        id="isActive"
                        value={localQuestion.isActive}
                        onChange={(e) => handleChange('isActive', e.target.value === 'true')}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                        required
                    >
                        <option value={true}>Active</option>
                        <option value={false}>Inactive</option>
                    </select>
                </div>
            </div>

            {/* Question Details Editor */}
            {localQuestion.details && (
                <QuestionDetailEditor
                    question={localQuestion}
                    onUpdateDetails={handleDetailsUpdate}
                />
            )}

            {/* Answer Choices Editor */}
            {(localQuestion.questionType === 'MULTIPLE_CHOICE' || localQuestion.questionType === 'TRUE_FALSE' || localQuestion.answerChoices) && (
                <AnswerChoiceEditor
                    answerChoices={localQuestion.answerChoices}
                    onUpdateChoices={handleAnswerChoicesUpdate}
                />
            )}
            <div className="pt-4 mt-6 border-t flex justify-end space-x-4">
                <button
                    onClick={onCancel}
                    className="px-6 py-2 bg-gray-500 text-white rounded-full hover:bg-gray-600 transition shadow-lg"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSubmit}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition shadow-lg"
                >
                    Save Changes
                </button>
            </div>
        </div>
    );
};


// ====================================================================
// 3. REFACTORED CONTAINER COMPONENTS
// ====================================================================

const JsonInputScreen = ({ jsonBuffer, setJsonBuffer, handleLoadJson }) => (
    <div className="flex flex-col items-center justify-center h-screen bg-gray-50 p-8">
        <div className="max-w-4xl w-full bg-white rounded-xl shadow-2xl p-10 space-y-8">
            <h1 className="text-4xl font-extrabold text-center text-indigo-700">Welcome to the Question Review App</h1>
            <p className="text-lg text-center text-gray-600">
                Please paste your question data in JSON array format below to begin.
            </p>
            <textarea
                value={jsonBuffer}
                onChange={(e) => setJsonBuffer(e.target.value)}
                rows={20}
                className="w-full p-6 border-2 border-dashed border-indigo-300 rounded-md font-mono text-sm resize-none focus:outline-none focus:border-indigo-500 transition"
                placeholder={`Paste your JSON array here, e.g.:\n\n[\n  {\n    "id": 1,\n    "questionType": "MULTIPLE_CHOICE",\n    "questionText": "What is 2+2?",\n    "source": { "id": 1 },\n    "answerChoices": [ ... ]\n  }\n]`}
            />
            <button
                onClick={handleLoadJson}
                className="w-full flex items-center justify-center space-x-2 px-6 py-3 text-lg text-white bg-indigo-600 rounded-full hover:bg-indigo-700 transition shadow-lg"
            >
                <Send size={20} />
                <span>Continue to Review</span>
            </button>
        </div>
    </div>
);

const QuestionList = ({ questions, selectedId, onSelect, onAddQuestion }) => (
    <div className="h-full flex flex-col">
        <div className="flex-grow overflow-y-auto">
            <ul className="space-y-2">
                {questions.map(q => (
                    <li key={q.id}>
                        <button
                            onClick={() => onSelect(q.id)}
                            className={`w-full text-left p-3 rounded-md transition duration-150 ease-in-out flex items-center justify-between ${selectedId === q.id ? 'bg-indigo-50 text-indigo-700 font-bold shadow-sm' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
                        >
                            <div>
                                <span className="text-sm font-medium">Question ID: {q.id}</span>
                                <p className="text-xs text-gray-400 truncate mt-1">
                                    Type: {q.questionType?.replace(/_/g, ' ') || 'N/A'}
                                </p>
                            </div>
                            {q.reviewStatus === 'approved' && (
                                <span className="flex-shrink-0 ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                    Approved
                                </span>
                            )}
                            {q.reviewStatus === 'rejected' && (
                                <span className="flex-shrink-0 ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                    Rejected
                                </span>
                            )}
                            {q.reviewStatus === 'pending' && (
                                <span className="flex-shrink-0 ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                                    Pending
                                </span>
                            )}
                        </button>
                    </li>
                ))}
            </ul>
        </div>
        <div className="mt-4 pt-4 border-t">
            <button
                onClick={onAddQuestion}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-sm bg-purple-500 text-white rounded-full hover:bg-purple-600 transition shadow-lg mx-auto"
            >
                <Plus size={16} />
                <span>Add New Question</span>
            </button>
        </div>
    </div>
);


const ReviewSidebar = ({
    questions,
    selectedQuestionId,
    onSelect,
    onAddQuestion,
    reviewedCount,
    totalCount,
    approvedCount,
    rejectedCount,
    showJsonEditor,
    setShowJsonEditor,
    handlePublish
}) => (

    <div className="w-1/4 min-w-[280px] border-r bg-white overflow-y-auto p-4 shadow-xl">
        <h2 className="text-2xl font-bold mb-4 text-indigo-800">Review Workflow</h2>
        <div className="mb-6 space-y-3">
            <button
                onClick={() => setShowJsonEditor(!showJsonEditor)}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-sm text-white bg-blue-500 rounded-full hover:bg-blue-600 transition shadow-lg"
            >
                <FileJson size={16} />
                <span>{showJsonEditor ? 'Hide Raw JSON' : 'View/Edit Raw JSON'}</span>
            </button>
            <button
                onClick={() => handlePublish(questions)}
                className={`w-full flex items-center justify-center space-x-2 px-4 py-2 text-sm text-white rounded-full transition shadow-lg ${reviewedCount === totalCount && totalCount > 0 ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 cursor-not-allowed'}`}
                disabled={reviewedCount < totalCount || totalCount === 0}
            >
                <Send size={16} />
                <span>Publish Questions ({reviewedCount}/{totalCount})</span>
            </button>
        </div>
        <div className="bg-gray-100 p-4 rounded-lg border border-gray-200 mb-6">
            <h3 className="text-lg font-bold text-gray-700 mb-2">Review Summary</h3>
            <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 bg-gray-200 rounded-md">
                    <div className="text-xl font-bold text-gray-800">{totalCount}</div>
                    <div className="text-xs text-gray-500">Total</div>
                </div>
                <div className="p-2 bg-green-200 rounded-md">
                    <div className="text-xl font-bold text-green-800">{approvedCount}</div>
                    <div className="text-xs text-green-700">Approved</div>
                </div>
                <div className="p-2 bg-red-200 rounded-md">
                    <div className="text-xl font-bold text-red-800">{rejectedCount}</div>
                    <div className="text-xs text-red-700">Rejected</div>
                </div>
            </div>
        </div>
        <hr className="my-4 border-gray-200" />
        <QuestionList
            questions={questions}
            selectedQuestionId={selectedQuestionId}
            onSelect={onSelect}
            onAddQuestion={onAddQuestion}
        />
    </div>
);


const MainContentArea = ({
    isReviewComplete,
    selectedQuestion,
    viewMode,
    setViewMode,
    validationErrors,
    handleUpdateQuestion,
    handleReviewStatusChange,
    showJsonEditor,
    jsonBuffer,
    setJsonBuffer,
    handleApplyJson,
    // Pass components as props to allow MainContentArea to render them
    QuestionEditor,
    AssessmentComponent,
    editorProps,
    handlePublish
}) => {

    // Final JSON Review Screen
    if (isReviewComplete) {
        return (
            <div className="flex-1 p-6 overflow-y-auto">
                <h1 className="text-4xl font-extrabold mb-8 text-indigo-700">Question Editor Dashboard</h1>
                <div className="p-8 bg-green-50 rounded-xl shadow-2xl border border-green-200 space-y-6 text-center">
                    <CheckCircle size={96} className="text-green-500 mx-auto" />
                    <h2 className="text-3xl font-bold text-green-700">Review Complete!</h2>
                    <p className="text-lg text-green-600">All questions have been reviewed and are ready to be published. The final JSON payload is displayed below.</p>
                    <textarea
                        readOnly
                        value={jsonBuffer}
                        rows={20}
                        className="w-full p-4 border border-green-300 rounded-md bg-green-100 font-mono text-sm resize-none"
                    />
                    <p
                        // onClick={() => { handlePublish() }}
                        className="px-6 py-3 text-lg text-white dark:text-black"
                    >
                        <Send size={20} className="inline mr-2" />
                        You can now publish the updated json
                    </p>
                </div>
            </div>
        );
    }

    // JSON Editor overlay
    const jsonEditorOverlay = showJsonEditor && (
        <div className="bg-red-50 p-6 rounded-xl mb-8 shadow-inner border border-red-200">
            <h3 className="text-2xl font-semibold text-red-700 mb-4">Raw JSON Buffer</h3>
            <textarea
                value={jsonBuffer}
                onChange={(e) => setJsonBuffer(e.target.value)}
                rows={20}
                className="w-full p-4 border border-red-300 rounded-md font-mono text-sm resize-none"
            />
            <button
                onClick={handleApplyJson}
                className="mt-4 px-6 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition shadow-lg"
            >
                Update Questions
            </button>
        </div>
    );

    return (
        <div className="flex-1 p-6 overflow-y-auto">
            <h1 className="text-4xl font-extrabold mb-8 text-indigo-700">Question Editor Dashboard</h1>
            {jsonEditorOverlay}
            {selectedQuestion ? (
                viewMode === 'preview' ? (
                    <AssessmentComponent
                        question={selectedQuestion}
                        onEdit={() => setViewMode('edit')}
                        onReviewStatusChange={(status) => handleReviewStatusChange(selectedQuestion, status)}
                        validationErrors={validationErrors}
                    />
                ) : (
                    <QuestionEditor
                        question={selectedQuestion}
                        onSave={handleUpdateQuestion}
                        onCancel={() => setViewMode('preview')}
                        {...editorProps}
                    />
                )
            ) : (
                <div className="flex flex-col items-center justify-center text-center text-gray-400 mt-20 p-8 border-2 border-dashed border-gray-300 rounded-xl">
                    <CheckCheck size={64} strokeWidth={1} />
                    <p className="mt-4 text-xl">Select a question from the left or load questions from the JSON editor to begin the review process.</p>
                </div>
            )}
        </div>
    );
};


// ====================================================================
// 4. MAIN CONTAINER COMPONENT (Refactored)
// ====================================================================

const QuestionsReviewApp = () => {
    // --- State Initialization (NO Mock Data) ---
    // Initialize with empty states, ready to load data from JSON buffer.
    const [questions, setQuestions] = useState([]);
    const [selectedQuestionId, setSelectedQuestionId] = useState(null);
    const [isJsonLoaded, setIsJsonLoaded] = useState(false);
    const [showJsonEditor, setShowJsonEditor] = useState(true);
    const [viewMode, setViewMode] = useState('preview');
    // Provide an empty array as a hint for the user
    const [jsonBuffer, setJsonBuffer] = useState('');
    const [validationErrors, setValidationErrors] = useState({});

    // API Data States
    const [sources, setSources] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [chapters, setChapters] = useState([]);
    const [classStandards, setClassStandards] = useState([]);
    const [tags, setTags] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // --- Core Logic Functions ---

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                // Fetch all metadata concurrently using the 'api' component (YOUR API CALLS ARE HERE)
                const [
                    sourcesResponse,
                    subjectsResponse,
                    chaptersResponse,
                    classStandardsResponse,
                    tagsResponse
                ] = await Promise.all([
                    api.get('/sources'),
                    api.get('/subjects'),
                    api.get('/chapters'),
                    api.get('/class-standards'),
                    api.get('/tags'),
                ]);

                // Assuming the API returns a response object with a 'data' field containing the array
                setSources(sourcesResponse.data || []);
                setSubjects(subjectsResponse.data || []);
                setChapters(chaptersResponse.data || []);
                setClassStandards(classStandardsResponse.data || []);
                setTags(tagsResponse.data || []);
            } catch (error) {
                // Fallback to empty arrays on fetch failure
                console.error("Failed to fetch metadata from API. Check api.js import and network.", error);
                setSources([]);
                setSubjects([]);
                setChapters([]);
                setClassStandards([]);
                setTags([]);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    // **IMPROVED VALIDATION LOGIC**
    const validateQuestion = useCallback((q, metadata) => {
        const errors = {};

        // 1. Mandatory Text Fields
        if (!q.questionText || q.questionText.trim() === '') {
            errors.questionText = 'Question text cannot be empty.';
        }
        if (!q.difficultyLevel || q.difficultyLevel.trim() === '') {
            errors.difficultyLevel = 'Difficulty Level is required.';
        }
        // isActive is mandatory but almost always true/false, checked for existence just in case.
        if (typeof q.isActive !== 'boolean') {
            errors.isActive = 'Active status is required.';
        }


        // 2. Mandatory Dropdown/Metadata Fields & ID Check
        const mandatoryMetadata = [
            { field: 'source', list: metadata.sources, name: 'Source' },
            { field: 'subject', list: metadata.subjects, name: 'Subject' },
            { field: 'chapter', list: metadata.chapters, name: 'Chapter' },
            { field: 'classStandard', list: metadata.classStandards, name: 'Class Standard' },
        ];

        mandatoryMetadata.forEach(item => {
            if (!q[item.field] || !q[item.field].id) {
                errors[item.field] = `${item.name} is mandatory.`;
            } else if (!item.list.some(m => m.id === q[item.field].id)) {
                errors[item.field] = `${item.name} ID (${q[item.field].id}) not found in master list.`;
            }
        });

        // 3. Sub-Question Details Check (Mandatory Text)
        if (q.questionType === 'MULTI_PART_COMPOSITE') {
            if (!q.details || q.details.length === 0) {
                errors.details = 'Multi-Part questions must have at least one sub-question detail.';
            } else {
                const emptyDetail = q.details.some(d => !d.questionChoice || d.questionChoice.trim() === '');
                if (emptyDetail) {
                    errors.details = 'All sub-questions in the details array must have text.';
                }
            }
        }


        // 4. Answer Choices Check (Mandatory Correct Answer)
        if (!q.answerChoices || q.answerChoices.length === 0) {
            errors.answerChoices = 'At least one answer choice is required.';
        } else {
            const hasCorrectAnswer = q.answerChoices.some(ac => ac.isCorrect);
            if (!hasCorrectAnswer) {
                errors.answerChoices = 'At least one answer choice must be marked as correct.';
            }
            const emptyChoice = q.answerChoices.some(ac => !ac.choiceText || ac.choiceText.trim() === '');
            if (emptyChoice) {
                errors.answerChoices = 'All answer choices must have text.';
            }
        }

        return errors;
    }, []);

    const handleLoadJson = () => {
        try {
            const sanitizedJson = jsonBuffer.replace(/\u00A0/g, ' ');
            const parsed = JSON.parse(sanitizedJson);
            if (!Array.isArray(parsed)) {
                window.alert('Invalid JSON format. Please provide an array of questions.');
                return;
            }
            if (parsed.length === 0) {
                window.alert('JSON array is empty. Please paste at least one question object.');
                return;
            }
            const questionsWithStatus = parsed.map((q, i) => ({
                // Ensure every question has an ID, using index if missing
                ...q,
                id: q.id || i,
                reviewStatus: q.reviewStatus || 'pending',
                tags: q.tags || [],
                details: q.details || [],
                answerChoices: q.answerChoices || []
            }));
            setQuestions(questionsWithStatus);
            setSelectedQuestionId(questionsWithStatus.length > 0 ? questionsWithStatus[0].id : null);
            setIsJsonLoaded(true);
            setViewMode('preview');
        } catch (error) {
            window.alert('Invalid JSON format. Please correct the syntax.');
            console.error('Invalid JSON format.', error);
        }
    };

    const handleApplyJson = () => {
        // Same logic as load but without requiring the initial load screen transition
        try {
            const sanitizedJson = jsonBuffer.replace(/\u00A0/g, ' ');
            const parsed = JSON.parse(sanitizedJson);
            if (!Array.isArray(parsed)) {
                window.alert('Invalid JSON format. Please provide an array of questions.');
                return;
            }
            if (parsed.length === 0) {
                window.alert('JSON array is empty. Please paste at least one question object.');
                return;
            }

            const questionsWithStatus = parsed.map((q, i) => ({
                ...q,
                id: q.id || i,
                reviewStatus: q.reviewStatus || 'pending',
                tags: q.tags || [],
                details: q.details || [],
                answerChoices: q.answerChoices || []
            }));
            setQuestions(questionsWithStatus);
            setSelectedQuestionId(questionsWithStatus.length > 0 ? questionsWithStatus[0].id : null);
            setShowJsonEditor(false);
            setViewMode('preview');
        } catch (error) {
            window.alert('Invalid JSON format. Please correct the syntax.');
            console.error('Invalid JSON format.', error);
        }
    };

    const handleUpdateQuestion = useCallback((updatedQuestion) => {
        setQuestions(prevQuestions => {
            const updatedList = prevQuestions.map(q =>
                q.id === updatedQuestion.id ? updatedQuestion : q
            );
            return updatedList;
        });
        setViewMode('preview');
    }, []);

    const handleAddQuestion = () => {
        const newId = Date.now();
        const newQuestion = {
            "id": newId,
            "questionType": "MULTI_PART_COMPOSITE",
            "questionText": "New Multi-Part Question Text here. Explain the graph.",
            "pictureUrl": "",
            "difficultyLevel": "LOW",
            "isActive": true,
            "answerChoices": [
                { "id": 1, "choiceText": "Summary Answer (a) 1 (b) 2 (c) 3", "isCorrect": true, "explanation": "" }
            ],
            "details": [
                { "id": Date.now() + 1, "questionChoice": "(a) First sub-question?", "explanation": "", "displayOrder": 1 }
            ],
            "source": null, "subject": null, "chapter": null, "classStandard": null, "tags": [],
            "reviewStatus": "pending"
        };
        setQuestions(prevQuestions => {
            const updatedList = [...prevQuestions, newQuestion];
            setSelectedQuestionId(newId);
            return updatedList;
        });
        setViewMode('edit');
    };

    const handlePublish = (qustions) => {
        console.log("--- FINAL JSON PAYLOAD FOR BACKEND ---");
        const questions = JSON.stringify(qustions, null, 2);
        try {
            const response = api.post("/questions/batch", questions, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            console.log('Successfully published questions:', response.data);
            setJsonBuffer('')
            setIsJsonLoaded(false);
        } catch (error) {
            window.alert('failed to update');

        }

        window.alert('All reviewed questions published to the backend! (Check console for payload)');
    };

    const handleReviewStatusChange = (question, status) => {
        const errors = validateQuestion(question, { sources, subjects, chapters, classStandards });
        if (Object.keys(errors).length > 0) {
            window.alert(`Cannot set status to ${status.toUpperCase()}. Please correct all validation errors first.`);
            return;
        }

        const updatedQuestion = { ...question, reviewStatus: status };
        setQuestions(prevQuestions => {
            const updatedList = prevQuestions.map(q =>
                q.id === updatedQuestion.id ? updatedQuestion : q
            );
            const currentIndex = updatedList.findIndex(q => q.id === updatedQuestion.id);

            // Find the next *pending* question
            let nextQuestionId = null;
            let foundNext = false;
            for (let i = currentIndex + 1; i < updatedList.length; i++) {
                if (updatedList[i].reviewStatus === 'pending') {
                    nextQuestionId = updatedList[i].id;
                    foundNext = true;
                    break;
                }
            }

            // If no pending found, just stay on the last reviewed question
            if (!foundNext && currentIndex < updatedList.length - 1) {
                nextQuestionId = updatedList[currentIndex].id; // Stay on current reviewed question
            }

            setSelectedQuestionId(nextQuestionId);

            return updatedList;
        });
        setViewMode('preview');
    };

    // --- Derived State ---
    const approvedCount = questions.filter(q => q.reviewStatus === 'approved').length;
    const rejectedCount = questions.filter(q => q.reviewStatus === 'rejected').length;
    const reviewedCount = approvedCount + rejectedCount;
    const totalCount = questions.length;
    const isReviewComplete = reviewedCount === totalCount && totalCount > 0;
    const selectedQuestion = questions.find(q => q.id === selectedQuestionId);

    // --- Effects ---
    useEffect(() => {
        // Keep the raw JSON buffer updated if the editor is open or questions change
        if (questions.length > 0) {
            setJsonBuffer(JSON.stringify(questions, null, 2));
        } else if (showJsonEditor) {
            setJsonBuffer('[\n  \n]');
        }
    }, [questions, showJsonEditor]);

    useEffect(() => {
        // Run validation on the selected question whenever it changes or metadata loads
        if (selectedQuestionId !== null && selectedQuestion) {
            const errors = validateQuestion(selectedQuestion, { sources, subjects, chapters, classStandards });
            setValidationErrors(errors);
        } else {
            setValidationErrors({});
        }
    }, [selectedQuestionId, selectedQuestion, sources, subjects, chapters, classStandards, validateQuestion]);


    // --- Render Logic (Simplified) ---

    if (!isJsonLoaded) {
        return (
            <Layout>
                <JsonInputScreen
                    jsonBuffer={jsonBuffer}
                    setJsonBuffer={setJsonBuffer}
                    handleLoadJson={handleLoadJson}
                />
            </Layout>
        );
    } else {

        return (
            <Layout>
                <div className="flex h-screen font-sans bg-gray-50">
                    <ReviewSidebar
                        questions={questions}
                        selectedQuestionId={selectedQuestionId}
                        onSelect={setSelectedQuestionId}
                        onAddQuestion={handleAddQuestion}
                        reviewedCount={reviewedCount}
                        totalCount={totalCount}
                        approvedCount={approvedCount}
                        rejectedCount={rejectedCount}
                        showJsonEditor={showJsonEditor}
                        setShowJsonEditor={setShowJsonEditor}
                        handlePublish={handlePublish}
                    />
                    <MainContentArea
                        isReviewComplete={isReviewComplete}
                        selectedQuestion={selectedQuestion}
                        viewMode={viewMode}
                        setViewMode={setViewMode}
                        validationErrors={validationErrors}
                        handleUpdateQuestion={handleUpdateQuestion}
                        handleReviewStatusChange={handleReviewStatusChange}
                        showJsonEditor={showJsonEditor}
                        jsonBuffer={jsonBuffer}
                        setJsonBuffer={setJsonBuffer}
                        handleApplyJson={handleApplyJson}
                        // Pass components and shared data for the editor
                        AssessmentComponent={AssessmentComponent}
                        QuestionEditor={QuestionEditor}
                        // handlePublish={handlePublish}
                        editorProps={{ sources, subjects, chapters, classStandards, tags, isLoading }}
                    />
                </div>
            </Layout>
        );
    }
};

export default QuestionsReviewApp;